import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskText } from "./Reveal";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/32597576/pexels-photo-32597576.jpeg?auto=compress&cs=tinysrgb&w=1600";

export const Hero = ({ start }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.85]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] w-full overflow-hidden bg-[#1a130b]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Khadi high fashion editorial" className="h-full w-full object-cover object-center" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#1a130b]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a130b] via-transparent to-[#1a130b]/30" />

      <motion.div style={{ y: textY }} className="relative z-10 h-full flex flex-col justify-center px-5 md:px-10 max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: start ? 1 : 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-[1px] w-10 bg-[#a65a34]" />
          <span className="font-display text-[10px] md:text-xs tracking-[0.35em] uppercase text-[#f2ece0]/80">
            A National Movement · Heritage · Sustainability · Innovation
          </span>
        </motion.div>

        <h1 className="font-serif uppercase text-[#f2ece0] tracking-tight leading-[0.86] text-[13vw] md:text-[9vw]">
          <MaskText start={start} delay={0.3} lines={["Wear India's", "Heritage."]} />
          <span className="block italic text-[#a65a34]">
            <MaskText start={start} delay={0.7} lines={["Walk India's Future."]} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: start ? 1 : 0, y: start ? 0 : 20 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-8 max-w-xl font-body text-sm md:text-base text-[#f2ece0]/70 leading-relaxed"
        >
          A pan-India initiative empowering designers, artisans, fashion institutes and youth
          through contemporary Khadi — reimagining India&apos;s most storied fabric for a global runway.
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: start ? 1 : 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#f2ece0]/60"
      >
        <span className="font-display text-[9px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
